import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Confirm from '@/components/common/Confirm'
import StateCitySelect from '@/components/common/StateCitySelect'
import { Alert, ErrorNote, Loading, PageHeader } from '@/components/common/Ui'
import { PERMS, can, useAuth } from '@/store/auth'

const TIERS = ['A', 'A+', 'B', 'B+', 'P']

const BLANK = {
  name: '',
  business_name: '',
  phone: '',
  gstin: '',
  price_tier: 'A',
  // Where most of this shop's customers are; changed in the form when not.
  city: 'Madurai',
  state: 'Tamil Nadu',
  pincode: '',
  alt_phone_1: '',
  billing_address: '',
}

const BLANK_ADDRESS = {
  label: '',
  address: '',
  state: 'Tamil Nadu',
  city: '',
  pincode: '',
  contact_name: '',
  contact_phone: '',
  is_default: false,
}

/**
 * Add or edit one customer, on a page of its own rather than a drawer: the
 * record has grown sections — details, billing, several delivery addresses —
 * and a drawer is the wrong shape for that much.
 *
 * Shipping addresses are saved one at a time as they are added, so they need
 * the customer to exist first. A new customer is saved, then the page becomes
 * their edit page with the addresses section open.
 */
export default function CustomerForm() {
  const { id } = useParams()
  const isNew = !id
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const editable = can(user, PERMS.customersWrite)

  const [form, setForm] = useState(isNew ? BLANK : null)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(
    location.state?.justCreated ? 'Customer added. Add their shipping addresses below.' : '',
  )

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customersApi.get(id),
    enabled: !isNew,
  })

  // Loaded once per customer. Later refetches — after an address is saved —
  // must not throw away details typed but not yet saved.
  useEffect(() => {
    if (data && (!form || form.id !== data.id)) setForm(data)
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (payload) =>
      isNew ? customersApi.create(payload) : customersApi.update(id, payload),
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.setQueryData(['customer', String(customer.id)], customer)
      setError('')
      if (isNew) {
        navigate(`/customers/${customer.id}`, {
          replace: true,
          state: { justCreated: true },
        })
      } else {
        setSaved('Saved.')
      }
    },
    onError: (e) => {
      setError(errorMessage(e))
      setSaved('')
    },
  })

  if (!isNew && isLoading) return <Loading />
  if (!isNew && isError) return <ErrorNote message="Could not load this customer" onRetry={refetch} />
  if (!form) return <Loading />

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  return (
    <div className="customer-form-page">
      <PageHeader
        title={isNew ? 'Add customer' : form.name || 'Customer'}
        subtitle={
          isNew
            ? 'The price tier decides what their orders are billed at.'
            : [form.business_name, form.phone].filter(Boolean).join(' · ')
        }
        actions={
          <Link to="/customers" className="btn btn-sm">
            Back to customers
          </Link>
        }
      />

      <Alert tone="success" message={saved} onClose={() => setSaved('')} />
      <Alert tone="danger" message={error} onClose={() => setError('')} />

      <form
        id="customer-form"
        onSubmit={(e) => {
          e.preventDefault()
          setSaved('')
          // Shipping addresses are saved on their own, below; the old single
          // block is kept in step with the default by the server.
          const { shipping_addresses: _addresses, shipping_address: _block, ...payload } = form
          save.mutate(payload)
        }}
      >
        {/* One block: the customer, their billing address, and Save. */}
        <section className="panel">
        <fieldset disabled={!editable} className="plain-fieldset">
            <div className="panel-title">Customer details</div>
            <div className="form-grid">
              <label className="field">
                <span>Contact Name *</span>
                <input value={form.name} onChange={(e) => set({ name: e.target.value })} required />
              </label>
              <label className="field">
                <span>Business name</span>
                <input
                  value={form.business_name ?? ''}
                  onChange={(e) => set({ business_name: e.target.value })}
                />
              </label>
              <label className="field">
                <span>WhatsApp number *</span>
                <input
                  value={form.phone}
                  onChange={(e) => set({ phone: e.target.value })}
                  placeholder="919876543210"
                  required
                  disabled={!isNew}
                />
              </label>
              <label className="field">
                <span>Alternative phone number</span>
                <input
                  value={form.alt_phone_1 ?? ''}
                  onChange={(e) => set({ alt_phone_1: e.target.value })}
                  placeholder="919876543211"
                />
              </label>
              <label className="field">
                <span>Price tier *</span>
                <select value={form.price_tier} onChange={(e) => set({ price_tier: e.target.value })}>
                  {TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>GSTIN</span>
                <input value={form.gstin ?? ''} onChange={(e) => set({ gstin: e.target.value })} />
              </label>
            </div>
            <p className="hint">Orders from the WhatsApp and alternative numbers are billed to this account.</p>

          <div className="panel-subsection">
            <div className="panel-title">Billing address</div>
            {/* The same fields, in the same order, as a shipping address. The
                state and city are the customer's own — what transport services
                are offered by — kept here beside the address they belong to. */}
            <div className="form-grid">
              <label className="field span-2">
                <span>Address</span>
                <textarea
                  rows={3}
                  value={form.billing_address ?? ''}
                  onChange={(e) => set({ billing_address: e.target.value })}
                  placeholder="Door no, street, area"
                />
              </label>
              {/* State first; the cities offered are that state's. */}
              <StateCitySelect
                state={form.state}
                city={form.city}
                onChange={({ state, city }) => set({ state, city })}
              />
              <label className="field">
                <span>Pincode</span>
                <input
                  value={form.pincode ?? ''}
                  onChange={(e) => set({ pincode: e.target.value })}
                  inputMode="numeric"
                  placeholder="625020"
                  maxLength={10}
                />
              </label>
            </div>
          </div>
        </fieldset>

        {editable && (
          <div className="form-actions panel-actions">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <Link to="/customers" className="btn">
              Cancel
            </Link>
          </div>
        )}
        </section>
      </form>

      {isNew ? (
        <section className="panel">
          <div className="panel-title">Shipping addresses</div>
          <p className="hint">Save the customer first, then add as many delivery addresses as they need.</p>
        </section>
      ) : (
        <ShippingAddresses
          customer={{ ...form, shipping_addresses: data?.shipping_addresses ?? [] }}
          editable={editable}
          billing={
            form.billing_address
              ? {
                  address: form.billing_address,
                  state: form.state ?? '',
                  city: form.city ?? '',
                  pincode: form.pincode ?? '',
                }
              : null
          }
        />
      )}
    </div>
  )
}

function ShippingAddresses({ customer, editable, billing }) {
  const queryClient = useQueryClient()
  // null: no form open; {} with no id: adding; with an id: editing that one.
  const [draft, setDraft] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [error, setError] = useState('')
  const addresses = customer.shipping_addresses ?? []

  const done = {
    onSuccess: (fresh) => {
      queryClient.setQueryData(['customer', String(customer.id)], (c) =>
        c ? { ...c, shipping_addresses: fresh } : c,
      )
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      setDraft(null)
      setRemoving(null)
      setError('')
    },
    onError: (e) => setError(errorMessage(e)),
  }
  const save = useMutation({
    mutationFn: ({ id, ...fields }) =>
      id
        ? customersApi.updateAddress(customer.id, id, fields)
        : customersApi.addAddress(customer.id, fields),
    ...done,
  })
  const makeDefault = useMutation({
    mutationFn: (id) => customersApi.updateAddress(customer.id, id, { is_default: true }),
    ...done,
  })
  const remove = useMutation({
    mutationFn: (id) => customersApi.removeAddress(customer.id, id),
    ...done,
  })

  return (
    <section className="panel">
      <div className="panel-title panel-title-row">
        Shipping addresses
        {editable && !draft && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() =>
              setDraft({ ...BLANK_ADDRESS, is_default: addresses.length === 0 })
            }
          >
            Add address
          </button>
        )}
      </div>
      <p className="hint">
        Where the goods can go. The default is printed on the order sheet; with none, the
        billing address is used.
      </p>
      <Alert tone="danger" message={error} onClose={() => setError('')} />

      {addresses.length === 0 && !draft && <p className="muted">No shipping addresses yet.</p>}

      <ul className="address-list">
        {addresses.map((a) => (
          <li key={a.id} className={`address-card${a.is_default ? ' is-default' : ''}`}>
            <div className="address-card-head">
              <strong>{a.label || a.contact_name || 'Shipping address'}</strong>
              {a.is_default && <span className="tag">Default</span>}
            </div>
            <div className="pre-line">{a.address}</div>
            {(a.city || a.state || a.pincode) && (
              <div className="muted">
                {[a.city, a.state].filter(Boolean).join(', ')}
                {a.pincode ? ` - ${a.pincode}` : ''}
              </div>
            )}
            {(a.contact_name || a.contact_phone) && (
              <div className="muted">
                Contact: {[a.contact_name, a.contact_phone].filter(Boolean).join(' · ')}
              </div>
            )}
            {editable && (
              <div className="address-card-actions">
                <button type="button" className="btn btn-sm" onClick={() => setDraft({ ...BLANK_ADDRESS, ...a })}>
                  Edit
                </button>
                {!a.is_default && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={makeDefault.isPending}
                    onClick={() => makeDefault.mutate(a.id)}
                  >
                    Make default
                  </button>
                )}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemoving(a)}>
                  Delete
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {draft && (
        <form
          className="address-form"
          onSubmit={(e) => {
            e.preventDefault()
            // No label field any more: an address that has one keeps it.
            const { id, address, state, city, pincode, contact_name, contact_phone, is_default } = draft
            save.mutate({ id, address, state, city, pincode, contact_name, contact_phone, is_default })
          }}
        >
          <div className="panel-title">{draft.id ? 'Edit address' : 'New address'}</div>
          <div className="form-grid">
            <label className="field">
              <span>Contact name</span>
              <input
                value={draft.contact_name ?? ''}
                onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
                placeholder="Who receives it"
              />
            </label>
            <label className="field">
              <span>Contact phone</span>
              <input
                type="tel"
                inputMode="tel"
                value={draft.contact_phone ?? ''}
                onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
                placeholder="98765 43210"
              />
            </label>
            <label className="field span-2">
              <span>Address *</span>
              <textarea
                rows={3}
                value={draft.address}
                onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                placeholder="Door no, street, area"
                required
              />
            </label>
            <StateCitySelect
              state={draft.state ?? ''}
              city={draft.city ?? ''}
              onChange={({ state, city }) => setDraft((d) => ({ ...d, state, city }))}
            />
            <label className="field">
              <span>Pincode</span>
              <input
                value={draft.pincode ?? ''}
                onChange={(e) => setDraft({ ...draft, pincode: e.target.value })}
                inputMode="numeric"
                placeholder="625020"
              />
            </label>
          </div>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={Boolean(draft.is_default)}
              disabled={draft.id && draft.is_default}
              onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })}
            />
            <span>Default shipping address</span>
          </label>
          <div className="address-form-actions">
            {!draft.id && billing && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setDraft({ ...draft, ...billing })}
              >
                Same as billing
              </button>
            )}
            <span className="spacer" />
            <button type="button" className="btn" onClick={() => setDraft(null)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save address'}
            </button>
          </div>
        </form>
      )}

      <Confirm
        open={Boolean(removing)}
        title="Delete this address?"
        message={
          removing?.is_default && addresses.length > 1
            ? `${removing?.label || 'This address'} is the default. The oldest remaining address becomes the default.`
            : `${removing?.label || 'This address'} will be removed from ${customer.name}.`
        }
        busy={remove.isPending}
        onConfirm={() => remove.mutate(removing.id)}
        onCancel={() => setRemoving(null)}
      />
    </section>
  )
}
